import { RBACService } from './rbac.service';

/**
 * A permission dependency: permissions on some features only work in the Web Portal when the role
 * also holds a permission on a parent feature, because the screens for them are only reachable
 * through the parent (inside a site, on an employee's page, on a contractor's page).
 *
 * Used by the RBAC role editor to warn when a role holds a dependent permission without its parent,
 * and to offer a one-click fix. It never grants anything on its own.
 *
 * Keep these rules in step with the real Web Portal gates (menu conditions, route guards and the
 * list buttons that open the parent page). If a gate changes, change the rule in the same pass.
 */
export interface RbacPermissionDependency {
  // Dependent feature slugs (RBACService.RBAC_F values) the rule applies to.
  features: string[];
  // Dependent actions the rule applies to. Omit to apply to every action on the feature.
  actions?: string[];
  // Permission strings that satisfy the rule. Holding ANY of them is enough. The first one present
  // in the catalogue is the one the editor adds when the user asks it to fix the gap.
  requiresAnyOf: string[];
  // Why the dependency exists, in plain language. Shown in the row tooltip.
  reason: string;
  // One-line hint shown under the category heading. Shared by every rule in the same category.
  hint: string;
}

const F = RBACService.RBAC_F;
const A = RBACService.RBAC_A;

// Every site-level feature. Derived from the F_SITES_ prefix so a new site feature is covered
// automatically: site features are always opened from inside a site.
const SITE_FEATURES: string[] = Object.keys(F)
  .filter((key: string): boolean => key.startsWith('F_SITES_'))
  .map((key: string): string => (F as Record<string, string>)[key]);

export const RBAC_PERMISSION_DEPENDENCIES: RbacPermissionDependency[] = [
  {
    // The sites list needs Sites: View to show any site, and the site menu items check it too.
    // Sites: Edit and Delete always include View (the editor ticks View with them).
    features: SITE_FEATURES,
    requiresAnyOf: [RBACService.getPermission(F.F_SITES, A.A_VIEW)],
    reason: 'Site features are opened from inside a site, so the role also needs Sites: View to find and open sites.',
    hint: 'Site features also need Sites: View, because they are opened from inside a site.'
  },
  {
    // The evacuation plan text is saved through the site update endpoint, which requires sites:edit,
    // and the plan's edit mode requires both (SitesEepEditComponent).
    features: [F.F_SITES_EEP],
    actions: [A.A_EDIT],
    requiresAnyOf: [RBACService.getPermission(F.F_SITES, A.A_EDIT)],
    reason: 'Changes to the emergency evacuation plan are saved with the site details, so editing it also needs Sites: Edit.',
    hint: 'Editing the Emergency Evacuation Plan also needs Sites: Edit.'
  },
  {
    // The employee page only opens from the Edit button on the employees list (Employees: Edit).
    // Access Requests is a separate tab on the employees list, so it is not listed here.
    features: [F.F_EMPLOYEES_TRAINING_COMPETENCIES, F.F_EMPLOYEES_SITE_ASSOCIATIONS],
    requiresAnyOf: [RBACService.getPermission(F.F_EMPLOYEES, A.A_EDIT)],
    reason: 'This is managed on the employee\'s page, which only opens with Employees: Edit.',
    hint: 'Training & Competencies and Site Associations also need Employees: Edit, because they are managed on the employee\'s page.'
  },
  {
    // The contractor page only opens from the Edit button on the contractors list (Contractors: Edit).
    features: [
      F.F_CONTRACTORS_SAFETY_PLANS,
      F.F_CONTRACTORS_DOCUMENTS,
      F.F_CONTRACTORS_PRE_QUALIFICATIONS,
      F.F_CONTRACTORS_SUBCONTRACTORS,
      F.F_CONTRACTORS_EMPLOYEES
    ],
    requiresAnyOf: [RBACService.getPermission(F.F_CONTRACTORS, A.A_EDIT)],
    reason: 'This is managed on the contractor\'s page, which only opens with Contractors: Edit.',
    hint: 'Contractor features also need Contractors: Edit, because they are managed on the contractor\'s page.'
  }
];
